import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BillingPeriod, BillingProviderStatus } from '@callhub/shared';
import { Button } from '@/components/ui-next';
import { api } from '@/lib/api';
import {
  buildBillingCsv,
  buildBillingExportFilename,
  buildBillingJson,
  formatBillingAmount,
  formatBillingDate,
  formatBillingUsage,
  labelForBillingPeriod,
  rollupBillingRecords,
  rollupTwilioCategoryRecords,
} from './billing-format';

const PERIODS: BillingPeriod[] = ['day', 'week', 'month'];

function providerStatusClasses(status: BillingProviderStatus): string {
  if (status === 'ok') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (status === 'partial') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  return 'bg-red-500/15 text-red-700 dark:text-red-300';
}

function providerStatusLabel(status: BillingProviderStatus): string {
  if (status === 'ok') return 'Operational';
  if (status === 'partial') return 'Partial';
  return 'Unavailable';
}

function formatBucket(value: string): string {
  if (/^\d{4}-W\d{2}$/.test(value)) {
    const [year, week] = value.split('-W');
    return `Week ${week}, ${year}`;
  }
  if (/^\d{4}-\d{2}$/.test(value)) {
    const parsed = new Date(`${value}-01T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(parsed);
  }
  return formatBillingDate(value);
}

export function BillingPage() {
  const [period, setPeriod] = useState<BillingPeriod>('day');

  const billingQuery = useQuery({
    queryKey: ['billing-usage', period],
    queryFn: async () => {
      const response = await api.getBillingUsage(period);
      return response.data;
    },
    staleTime: 45_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const data = billingQuery.data;
  const providerCount = data?.providers.length ?? 0;
  const serviceRollups = useMemo(() => rollupBillingRecords(data?.records ?? []), [data?.records]);
  const twilioCategoryRollups = useMemo(
    () => rollupTwilioCategoryRecords(data?.records ?? []),
    [data?.records],
  );
  const healthyProviderCount = useMemo(
    () => data?.providers.filter((provider) => provider.status === 'ok').length ?? 0,
    [data?.providers],
  );

  const handleExport = (format: 'csv' | 'json') => {
    if (!data || typeof window === 'undefined') return;

    const content = format === 'csv' ? buildBillingCsv(data) : buildBillingJson(data);
    const filename = buildBillingExportFilename(period, data.generatedAt, format);
    const mimeType = format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8';
    const blob = new Blob([content], { type: mimeType });
    const href = window.URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.click();
    window.URL.revokeObjectURL(href);
  };

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Billing</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Detailed usage and cost by provider/service with period summaries.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-1">
            {PERIODS.map((option) => (
              <Button
                key={option}
                variant={option === period ? 'primary' : 'ghost'}
                onClick={() => setPeriod(option)}
                className={option === period ? '' : 'text-muted-foreground'}
              >
                {labelForBillingPeriod(option)}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-1">
            <Button
              variant="ghost"
              onClick={() => handleExport('csv')}
              disabled={!data || data.records.length === 0}
              className="text-muted-foreground disabled:opacity-40"
            >
              Export CSV
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleExport('json')}
              disabled={!data}
              className="text-muted-foreground disabled:opacity-40"
            >
              Export JSON
            </Button>
          </div>
        </div>
      </header>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Cost</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {data
              ? formatBillingAmount(data.summary.totalCost, data.summary.currency)
              : billingQuery.isPending
                ? '...'
                : 'N/A'}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Billable Records</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {data ? data.summary.billableRecords : billingQuery.isPending ? '...' : '0'}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Rows</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {data ? data.summary.totalRecords : billingQuery.isPending ? '...' : '0'}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Providers Ready</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {healthyProviderCount}/{providerCount}
          </p>
        </div>
      </div>

      {data ? (
        <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {data.providers.map((provider) => (
            <div key={provider.provider} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-foreground">{provider.label}</h3>
                <span
                  className={`rounded px-2 py-1 text-xs ${providerStatusClasses(provider.status)}`}
                >
                  {providerStatusLabel(provider.status)}
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Cost: {formatBillingAmount(provider.totalCost, provider.currency)} • Records:{' '}
                {provider.records}
              </p>
              {provider.errors.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {provider.errors.map((error) => (
                    <li key={error} className="text-xs text-red-500">
                      {error}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {data?.notes.length ? (
        <div className="mb-5 rounded-xl border border-border bg-card p-4">
          <h3 className="text-sm font-semibold text-foreground">Notes</h3>
          <ul className="mt-2 space-y-1">
            {data.notes.map((note) => (
              <li key={note} className="text-xs text-muted-foreground">
                {note}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {serviceRollups.length > 0 ? (
        <section className="mb-5 overflow-hidden rounded-xl border border-border bg-card">
          <header className="border-b border-border px-4 py-3">
            <h3 className="text-base font-semibold text-foreground">Service Totals</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Aggregated by provider and service for the selected period.
            </p>
          </header>
          <div className="max-h-[28vh] overflow-auto">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Provider</th>
                  <th className="px-4 py-2 font-medium">Service</th>
                  <th className="px-4 py-2 font-medium">Rows</th>
                  <th className="px-4 py-2 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {serviceRollups.map((rollup) => (
                  <tr key={rollup.key} className="border-b border-border/80 text-foreground">
                    <td className="px-4 py-2 align-top capitalize">{rollup.provider}</td>
                    <td className="px-4 py-2 align-top">{rollup.service}</td>
                    <td className="px-4 py-2 align-top">{rollup.records}</td>
                    <td className="px-4 py-2 align-top">
                      {formatBillingAmount(rollup.totalCost, rollup.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {twilioCategoryRollups.length > 0 ? (
        <section className="mb-5 overflow-hidden rounded-xl border border-border bg-card">
          <header className="border-b border-border px-4 py-3">
            <h3 className="text-base font-semibold text-foreground">Twilio Category Totals</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Aggregated Twilio usage categories and covered service breadth.
            </p>
          </header>
          <div className="max-h-[24vh] overflow-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Services</th>
                  <th className="px-4 py-2 font-medium">Rows</th>
                  <th className="px-4 py-2 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {twilioCategoryRollups.map((rollup) => (
                  <tr key={rollup.key} className="border-b border-border/80 text-foreground">
                    <td className="px-4 py-2 align-top">{rollup.category}</td>
                    <td className="px-4 py-2 align-top">{rollup.services}</td>
                    <td className="px-4 py-2 align-top">{rollup.records}</td>
                    <td className="px-4 py-2 align-top">
                      {formatBillingAmount(rollup.totalCost, rollup.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-base font-semibold text-foreground">Detailed Records</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Range:{' '}
            {data
              ? `${data.range.startDate} - ${data.range.endDate}`
              : labelForBillingPeriod(period)}
          </p>
        </header>

        {billingQuery.isPending ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Loading billing data...</p>
        ) : billingQuery.isError ? (
          <p className="px-4 py-6 text-sm text-red-500">
            Failed to load billing data. Check Twilio/Cloudflare credentials and token scopes.
          </p>
        ) : !data || data.records.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            No billing records returned for the selected period.
          </p>
        ) : (
          <div className="max-h-[52vh] overflow-auto">
            <table className="w-full min-w-[960px] border-collapse text-left text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Bucket</th>
                  <th className="px-4 py-2 font-medium">Provider</th>
                  <th className="px-4 py-2 font-medium">Service</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Usage</th>
                  <th className="px-4 py-2 font-medium">Cost</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((record) => (
                  <tr key={record.id} className="border-b border-border/80 text-foreground">
                    <td className="px-4 py-2 align-top">{formatBucket(record.bucket)}</td>
                    <td className="px-4 py-2 align-top capitalize">{record.provider}</td>
                    <td className="px-4 py-2 align-top">{record.service}</td>
                    <td className="px-4 py-2 align-top">
                      <p>{record.category}</p>
                      {record.description ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">{record.description}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 align-top">{formatBillingUsage(record)}</td>
                    <td className="px-4 py-2 align-top">
                      {formatBillingAmount(record.cost, record.currency)}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-muted-foreground">
                      {record.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
